import { LangToggle, useT } from "./i18n.jsx";
import { CalendarIcon, HomeIcon, ImageIcon, LayersIcon, LayoutTemplate, Logo, MenuIcon, PanelLeftClose, ShieldIcon, StarIcon, T } from "./theme.jsx";
import { AccountMenu } from "./team/AccountMenu.jsx";

const NAV_ITEMS = [
  { key: "home", labelKey: "navHome", icon: HomeIcon },
  { key: "calendar", labelKey: "navCalendar", icon: CalendarIcon },
  { key: "master", labelKey: "navMaster", icon: StarIcon },
  { key: "template", labelKey: "navTemplate", icon: LayoutTemplate },
  { key: "pool", labelKey: "navCuePool", icon: LayersIcon },
  { key: "photos", labelKey: "navPhotoLibrary", icon: ImageIcon },
];
const ADMIN_NAV_ITEM = { key: "admin", labelKey: "navAdmin", icon: ShieldIcon };
const navItemsFor = (perms) => (perms && perms.isAdmin ? [...NAV_ITEMS, ADMIN_NAV_ITEM] : NAV_ITEMS);

function ModeTab({ icon: Ic, label, active, onClick }) {
  return (
    <button className="rb-btn" onClick={onClick} aria-current={active ? "page" : undefined}
      style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 4, border: "none", background: active ? T.accent : T.darkChip, color: active ? T.accentText : T.darkSubtext, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
      <Ic size={13} /> {label}
    </button>
  );
}

// desk/iPad: a persistent left rail replaces the old top tab bar so the
// venue name, live status, and nav are always visible without scrolling up.
function Sidebar({ mode, setMode, viewer, perms, onSignOut, liveNowKey, onGoLive, collapsed, onToggleCollapsed }) {
  const { t } = useT();
  const w = collapsed ? 72 : 236;
  return (
    <aside className="rb-sidebar" aria-label={t("venueName")} style={{
      width: w, flex: `0 0 ${w}px`, background: T.dark, borderRight: "1px solid #262626",
      display: "flex", flexDirection: "column", position: "sticky", top: 0, height: "100vh",
      overflow: "hidden",
    }}>
      <div style={{ padding: collapsed ? "18px 0" : "18px 16px 14px", display: "flex", alignItems: "center", gap: 10, justifyContent: collapsed ? "center" : "flex-start" }}>
        <Logo size={34} />
        {!collapsed && (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: T.darkText, letterSpacing: 0.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t("venueName")}</div>
            <div style={{ fontSize: 11, color: T.darkSubtext, whiteSpace: "nowrap" }}>{t("venueSubtitle")}</div>
          </div>
        )}
      </div>

      {liveNowKey && (
        <button className="rb-btn" onClick={() => onGoLive(liveNowKey)} title={collapsed ? `${t("liveNow")} · ${liveNowKey}` : undefined}
          style={{
            margin: collapsed ? "2px 10px 12px" : "2px 14px 12px", padding: collapsed ? "8px 0" : "9px 12px",
            display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 8,
            background: "#C1272D", border: "1px solid #FF6B62", borderRadius: 8, color: "#fff", cursor: "pointer",
            fontSize: 12, fontWeight: 800,
          }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", animation: "rb-pulse 1.2s infinite", flex: "0 0 auto" }} />
          {!collapsed && <span>{t("liveNow")}</span>}
        </button>
      )}

      <nav aria-label="Main" style={{ flex: 1, overflowY: "auto", padding: "4px 10px" }}>
        {navItemsFor(perms).map((item) => (
          <button key={item.key} className="rb-btn" onClick={() => setMode(item.key)} title={collapsed ? t(item.labelKey) : undefined}
            aria-label={collapsed ? t(item.labelKey) : undefined} aria-current={mode === item.key ? "page" : undefined}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 10, textAlign: "left",
              padding: collapsed ? "10px 0" : "9px 11px", marginBottom: 3, borderRadius: 8, border: "none",
              justifyContent: collapsed ? "center" : "flex-start",
              background: mode === item.key ? T.accent : "transparent",
              color: mode === item.key ? T.accentText : T.darkSubtext,
              fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}>
            <item.icon size={15} />
            {!collapsed && <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t(item.labelKey)}</span>}
          </button>
        ))}
      </nav>

      <div style={{ margin: collapsed ? "0 10px 8px" : "0 14px 8px", display: "flex", justifyContent: collapsed ? "center" : "flex-start" }}>
        <LangToggle compact={collapsed} />
      </div>

      <button className="rb-btn" onClick={onToggleCollapsed} aria-expanded={!collapsed} aria-label={collapsed ? t("collapseMenu") : undefined}
        style={{ margin: "0 10px 10px", padding: "8px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "transparent", border: "1px solid #2C2C2C", borderRadius: 8, color: T.darkSubtext, cursor: "pointer", fontSize: 11 }}>
        {collapsed ? <MenuIcon size={14} /> : <React.Fragment><PanelLeftClose size={14} /> {t("collapseMenu")}</React.Fragment>}
      </button>

      <div style={{ borderTop: "1px solid #262626", padding: collapsed ? "12px 0" : "10px 10px", display: "flex", alignItems: "center", gap: 8, justifyContent: collapsed ? "center" : "flex-start" }}>
        {onSignOut ? <AccountMenu viewer={viewer} onSignOut={onSignOut} compact={collapsed} /> : (
          <>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: T.darkChip, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: T.darkText, flex: "0 0 auto" }}>
              {(viewer.name || "?").slice(0, 1).toUpperCase()}
            </div>
            {!collapsed && (
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.darkText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{viewer.name || t("guestUser")}</div>
                <div style={{ fontSize: 11, fontWeight: 800, color: T.accent }}>{viewer.roleLabel}</div>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}

// phone-width fallback: the original dark top bar + tab row, shown only under
// the CSS breakpoint that hides .rb-sidebar (see the <style> block in index.html)
function MobileTopBar({ mode, setMode, viewer, perms, onSignOut }) {
  const { t } = useT();
  return (
    <div className="rb-mobile-topbar" style={{ background: T.dark, padding: "12px 16px", borderBottom: `1px solid ${T.border}`, position: "sticky", top: "env(safe-area-inset-top, 0px)", zIndex: 30, textAlign: "center" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, textAlign: "left" }}>
          <Logo size={32} />
          <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.darkText, letterSpacing: 0.2 }}>{t("venueName")}</div>
          <div style={{ fontSize: 11.5, color: T.darkSubtext }}>{t("venueTopbarSub")}</div>
          </div>
        </div>
        {onSignOut ? <AccountMenu viewer={viewer} onSignOut={onSignOut} /> : (
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.darkChip, borderRadius: 6, padding: "6px 11px" }}>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: T.darkText }}>{viewer.name || t("guestUser")}</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: T.accent, background: "rgba(255,90,31,0.16)", borderRadius: 4, padding: "2px 6px" }}>{viewer.roleLabel}</span>
          </div>
        )}
        <LangToggle compact />
      </div>
      <nav aria-label="Main" style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
        {navItemsFor(perms).map((item) => (
          <ModeTab key={item.key} icon={item.icon} label={t(item.labelKey)} active={mode === item.key} onClick={() => setMode(item.key)} />
        ))}
      </nav>
    </div>
  );
}

export { NAV_ITEMS, ModeTab, Sidebar, MobileTopBar };
